import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Crown, Medal, School, Trophy, Brain } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { flagUrl } from "@/lib/nationalities";
import ShareDiplomaButton from "@/components/ShareDiplomaButton";

const RANKING_TYPES = [
  { value: "vs_time",  key: "mode_vs_time"  },
  { value: "vs_robot", key: "mode_vs_robot" },
  { value: "vs_human", key: "mode_vs_human" },
];

/** Reads the current player name from sessionStorage (any known key). */
export function useMyName() {
  return React.useMemo(() => {
    try {
      const raw = sessionStorage.getItem("player_name")
                || sessionStorage.getItem("vs_robot_name")
                || "";
      return raw.trim();
    } catch { return ""; }
  }, []);
}

/** Case-insensitive/whitespace-tolerant name match. */
export function sameName(a, b) {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/** Small yellow "és tu / it's you / eres tú" badge appended to the player name. */
export function MeBadge({ lang }) {
  const label = lang === "en" ? "it’s you" : lang === "es" ? "eres tú" : "és tu";
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide bg-amber-400 text-slate-900 ring-1 ring-amber-500 shadow-sm"
      data-testid="rank-me-badge"
    >
      ★ {label}
    </span>
  );
}

export default function Rankings() {
  const { t, lang } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  // Ranking type selector — persisted in the URL so it survives refresh &
  // deep-links from the header button (e.g. `/rankings?type=vs_human`).
  const rawType = (searchParams.get("type") || "vs_time").toLowerCase();
  const type = RANKING_TYPES.some((r) => r.value === rawType) ? rawType : "vs_time";
  const setType = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set("type", v);
    // Reset ranking-type-specific params so switching feels clean.
    ["game", "level", "mode"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const currentLabel = t(RANKING_TYPES.find((r) => r.value === type)?.key || "mode_vs_time");

  return (
    <SiteChrome>
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-[Fredoka] font-semibold text-white mb-4" data-testid="rankings-page-title">
          {t("ranking_page_title")} — {currentLabel}
        </h1>

        {/* Combo box to switch between ranking families */}
        <div className="mb-6 max-w-sm">
          <label className="text-xs font-bold text-white/70 uppercase tracking-wider">
            {t("ranking_type_label")}
          </label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="rank-type-select" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANKING_TYPES.map((r) => (
                <SelectItem key={r.value} value={r.value} data-testid={`rank-type-option-${r.value}`}>
                  {t(r.key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {type === "vs_time"  && <VsTimeRanking t={t} lang={lang} searchParams={searchParams} />}
        {type === "vs_robot" && <VsRobotRanking t={t} lang={lang} />}
        {type === "vs_human" && <VsHumanRanking t={t} lang={lang} />}
      </div>
    </SiteChrome>
  );
}

/* ---------------- VS TEMPO / COMPETIÇÃO ---------------- */

function VsTimeRanking({ t, lang, searchParams }) {
  const myName = useMyName();
  const initialGame = searchParams.get("game") || "";
  const initialLevel = searchParams.get("level") || "all";
  const initialTier = searchParams.get("tier") || "all";
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState(initialGame);
  const [level, setLevel] = useState(initialLevel);
  // Tier state — used when the game has `level_system="tiers"`. Tracked
  // separately from `level` because two tiers can share the same numeric
  // level (e.g. 4.º ano and 5.º ano both at level 3), which would collapse
  // the dropdown and break the ranking filter otherwise.
  const [tierId, setTierId] = useState(initialTier);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    // VS TEMPO ranking is exclusive to training games — competition and
    // eliminatorias have their own ranking flows. Always scope the game
    // picker to `status=training` regardless of the caller's `mode` param.
    const params = { lang, status: "training" };
    api.get("/results/public/games", { params }).then((r) => {
      setGames(r.data);
      if (r.data.length) {
        const wanted = initialGame && r.data.find((g) => g.id === initialGame);
        setGameId(wanted ? initialGame : r.data[0].id);
      } else {
        setGameId("");
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const currentGame = useMemo(() => games.find((g) => g.id === gameId), [games, gameId]);
  const isCompetition = false; // VS TEMPO is training-only after 2026-02 fix
  const useTiers = currentGame?.level_system === "tiers" && Array.isArray(currentGame?.tiers);

  useEffect(() => {
    if (!gameId || !currentGame) { setRows([]); return; }
    const base = isCompetition ? `/rankings/competition/${gameId}` : `/rankings/training/${gameId}`;
    let url = base;
    if (useTiers && tierId !== "all") {
      url = `${base}?tier_id=${encodeURIComponent(tierId)}`;
    } else if (!useTiers && level !== "all") {
      url = `${base}?level=${parseInt(level, 10)}`;
    }
    api.get(url).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [gameId, currentGame, isCompetition, level, tierId, useTiers]);

  const topN = currentGame?.top_n ?? 10;

  const filterAndLimit = (data) => {
    const list = data || [];
    const n = Number(topN) || 0;
    return n > 0 ? list.slice(0, n) : list;
  };

  const buildColumns = () => {
    const cols = ["ranking"];
    if (currentGame?.show_name) cols.push("name");
    cols.push("flag");
    cols.push("school");
    if (level === "all") cols.push("level");
    if (currentGame?.show_best_time) cols.push("best_time");
    if (isCompetition) cols.push("share");
    return cols;
  };

  return (
    <>
      {/* Optional subtitle text — per-game, editable in /admin/utilizadores-vs-tempo. */}
      {(() => {
        const sub = currentGame?.[`subtitle_${lang}`] || currentGame?.subtitle_pt || "";
        return sub ? (
          <p className="text-white/80 text-sm md:text-base font-[Nunito] mb-6 max-w-2xl" data-testid="rankings-subtitle">
            {sub}
          </p>
        ) : <div className="mb-4" />;
      })()}

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[260px] max-w-sm">
          <label className="text-xs font-bold text-white/70 uppercase">{t("col_game")}</label>
          <Select value={gameId} onValueChange={setGameId}>
            <SelectTrigger data-testid="rank-game"><SelectValue placeholder={t("select_game")} /></SelectTrigger>
            <SelectContent>
              {games.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <label className="text-xs font-bold text-white/70 uppercase">
            {useTiers ? t("col_level_or_tier") : t("col_level")}
          </label>
          <Select
            value={useTiers ? tierId : level}
            onValueChange={(v) => {
              if (useTiers) {
                setTierId(v);
              } else {
                setLevel(v);
              }
            }}
          >
            <SelectTrigger data-testid="rank-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{useTiers ? t("all_tiers") : t("all_levels")}</SelectItem>
              {useTiers ? (
                currentGame.tiers.map((tier) => (
                  <SelectItem key={tier.id} value={tier.id}>{tier.name}</SelectItem>
                ))
              ) : (
                (currentGame?.format === "quiz" ? [1, 2, 3, 4] : [1, 2, 3, 4, 5]).map((l) => (
                  <SelectItem key={l} value={String(l)}>{t("level")} {l}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!games.length ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500" data-testid="no-results">
          {lang === "en" ? "No games with public results." : lang === "es" ? "No hay juegos con resultados públicos." : "Sem jogos com resultados públicos."}
        </div>
      ) : (
        <RankTable rows={filterAndLimit(rows)} columns={buildColumns()} game={currentGame} t={t} lang={lang} myName={myName} />
      )}
    </>
  );
}

export function RankTable({ rows, columns, game, t, lang, myName }) {
  const useTiers = game?.level_system === "tiers" && Array.isArray(game?.tiers);
  const headerLabel = (c) => {
    switch (c) {
      case "ranking": return "#";
      case "name": return t("col_name");
      case "flag": return "";
      case "level": return useTiers ? t("col_level_or_tier") : t("col_level");
      case "school": return t("col_school");
      case "best_time": return t("col_best_time");
      case "share": return t("col_share");
      default: return c;
    }
  };
  const noData = lang === "en" ? "No data" : lang === "es" ? "Sin datos" : "Sem dados";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {columns.map((c) => (
              <th key={c} className="text-left px-4 py-3 font-bold text-slate-700 uppercase text-xs tracking-wider">
                {headerLabel(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody data-testid="ranking-table">
          {rows.map((r, i) => {
            const isMe = sameName(r?.name, myName);
            return (
            <tr
              key={r.id || i}
              className={`border-b border-slate-100 ${isMe ? "bg-amber-100 hover:bg-amber-100 ring-1 ring-amber-400" : "hover:bg-slate-50"}`}
              data-testid={isMe ? `rank-row-me-${i}` : `rank-row-${i}`}
            >
              {columns.map((c) => (
                <td key={c} className="px-4 py-3 text-slate-700">
                  {c === "ranking" ? (
                    <RankBadge rank={r.ranking ?? i + 1} />
                  ) : c === "best_time" ? (
                    r.best_time != null ? `${Number(r.best_time).toFixed(1)}s` : "-"
                  ) : c === "flag" ? (
                    r.nationality && flagUrl(r.nationality) ? (
                      <img src={flagUrl(r.nationality)} alt={r.nationality} className="w-6 h-4 object-cover rounded-sm ring-1 ring-slate-200" />
                    ) : null
                  ) : c === "level" ? (
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-xs font-bold bg-sky-100 text-sky-700 ring-1 ring-sky-300" data-testid={`rank-level-${i}`}>
                      {useTiers
                        ? (() => {
                            const tr = (r.tier_id && game.tiers.find((x) => x.id === r.tier_id))
                              || game.tiers.find((x) => Number(x.level) === Number(r.level));
                            return tr ? tr.name : `${t("level")} ${r.level ?? 1}`;
                          })()
                        : `${t("level")} ${r.level ?? 1}`}
                    </span>
                  ) : c === "share" ? (
                    r.id ? (
                      <ShareDiplomaButton credentialId={r.id} variant="ghost" label={t("col_share")} />
                    ) : "-"
                  ) : c === "name" ? (
                    <span>
                      {r[c] ?? "-"}
                      {isMe && <MeBadge lang={lang} />}
                    </span>
                  ) : (
                    r[c] ?? "-"
                  )}
                </td>
              ))}
            </tr>
            );
          })}
          {!rows.length && (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-slate-400">{noData}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RankBadge({ rank }) {
  const cls =
    rank === 1
      ? "bg-amber-100 text-amber-700 ring-1 ring-amber-400"
      : rank === 2
        ? "bg-slate-200 text-slate-700"
        : rank === 3
          ? "bg-orange-100 text-orange-700"
          : "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center justify-center min-w-[28px] h-6 rounded-full text-xs font-bold ${cls}`}>{rank}</span>
  );
}

/* ---------------- VS ROBOT ---------------- */

function VsRobotRanking({ t, lang }) {
  const myName = useMyName();
  const [data, setData] = useState({ rows: [], title: "", subtitle: "", intro: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/vs-robot/leaderboard?lang=${lang}`)
      .then((r) => { setData(r.data || { rows: [] }); setError(""); })
      .catch((e) => setError(e?.response?.data?.detail || (lang === "en" ? "Failed to load ranking." : lang === "es" ? "No se pudo cargar el ranking." : "Não foi possível carregar o ranking.")))
      .finally(() => setLoading(false));
  }, [lang]);

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const title = data?.title || "TOP Caçadores de Robots";
  const subtitle = data?.subtitle || (lang === "en" ? "The best robot hunters" : lang === "es" ? "Los mejores cazadores de robots" : "Os melhores caçadores de robots");

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="vsr-ranking-loading">
        {t("common_loading")}
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-rose-500" data-testid="vsr-ranking-error">
        {error}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="vsr-ranking-empty">
        <Crown className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <h3 className="text-lg font-[Fredoka] font-bold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 shadow-md border border-amber-200 p-6" data-testid="vsr-ranking">
      <div className="flex items-center gap-3 mb-1">
        <Crown className="w-7 h-7 text-amber-500" />
        <h2 className="text-2xl font-[Fredoka] font-bold text-slate-800" data-testid="vsr-ranking-title">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-slate-500 mb-4 pl-10">{subtitle}</p>}
      <ol className="space-y-1.5">
        {rows.map((row, i) => {
          const rank = i + 1;
          const isPodium = rank <= 3;
          const isMe = sameName(row?.player_name, myName);
          const medalColor =
            rank === 1 ? "text-amber-500" :
            rank === 2 ? "text-slate-400" :
            rank === 3 ? "text-orange-400" : "text-slate-300";
          const pts = Number(row.points || 0);
          return (
            <li
              key={row.player_name || i}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                isMe
                  ? "bg-amber-100 border-amber-400 ring-2 ring-amber-400 shadow-sm"
                  : isPodium
                    ? "bg-white border-amber-200 shadow-sm"
                    : "bg-white/60 border-slate-200"
              }`}
              data-testid={isMe ? `vsr-ranking-row-me-${rank}` : `vsr-ranking-row-${rank}`}
            >
              <div className="w-8 flex justify-center">
                {isPodium ? <Medal className={`w-5 h-5 ${medalColor}`} /> : <span className="text-sm font-bold text-slate-500">#{rank}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-[Fredoka] font-bold text-slate-800 truncate flex items-center" data-testid={`vsr-ranking-name-${rank}`}>
                  <span className="truncate">{row.player_name}</span>
                  {isMe && <MeBadge lang={lang} />}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  {row.school && (
                    <span className="inline-flex items-center gap-1">
                      <School className="w-3 h-3" /> {row.school}
                    </span>
                  )}
                  <span>{row.total} {row.total === 1 ? t("duel_one") : t("duel_many")}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-extrabold font-[Fredoka] ${pts >= 0 ? "bg-violet-500 text-white" : "bg-rose-500 text-white"}`}
                  data-testid={`vsr-ranking-points-${rank}`}
                >
                  <Brain className="w-3 h-3" /> {pts >= 0 ? "+" : ""}{Math.round(pts * 10) / 10} pts
                </span>
                <div className="flex gap-1 text-[10px] font-bold">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                    <Trophy className="w-2.5 h-2.5" /> {row.wins}
                  </span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                    ✗ {row.losses}
                  </span>
                  {row.ties > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      = {row.ties}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ---------------- VS HUMANO ---------------- */

function VsHumanRanking({ t, lang }) {
  const myName = useMyName();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    // Omit `limit` so the backend honours the admin-configured
    // `public_limit` from vs_human_config (user request 2026-02).
    api.get(`/vs-human/leaderboard`)
      .then((r) => { setRows(Array.isArray(r.data) ? r.data : []); setError(""); })
      .catch((e) => setError(e?.response?.data?.detail || (lang === "en" ? "Failed to load ranking." : lang === "es" ? "No se pudo cargar el ranking." : "Não foi possível carregar o ranking.")))
      .finally(() => setLoading(false));
  }, [lang]);

  const title = lang === "en" ? "TOP Human Duellists"
              : lang === "es" ? "TOP Duelistas Humanos"
              : "TOP Duelistas Humanos";
  const modeName = t("mode_vs_human");
  const subtitle = lang === "en" ? `The best ${modeName} players`
                 : lang === "es" ? `Los mejores jugadores ${modeName}`
                 : `Os melhores jogadores ${modeName}`;

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="vsh-ranking-loading">
        {t("common_loading")}
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-rose-500" data-testid="vsh-ranking-error">
        {error}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="vsh-ranking-empty">
        <Crown className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <h3 className="text-lg font-[Fredoka] font-bold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        <p className="text-xs text-slate-400 mt-2">
          {lang === "en" ? `Play a ${modeName} duel to appear here!` : lang === "es" ? `¡Juega un duelo ${modeName} para aparecer aquí!` : `Joga um duelo ${modeName} para apareceres aqui!`}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-gradient-to-br from-sky-50 via-indigo-50 to-fuchsia-50 shadow-md border border-sky-200 p-6" data-testid="vsh-ranking">
      <div className="flex items-center gap-3 mb-1">
        <Crown className="w-7 h-7 text-sky-500" />
        <h2 className="text-2xl font-[Fredoka] font-bold text-slate-800" data-testid="vsh-ranking-title">{title}</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4 pl-10">{subtitle}</p>
      <ol className="space-y-1.5">
        {rows.map((row, i) => {
          const rank = i + 1;
          const isPodium = rank <= 3;
          const isMe = sameName(row?.name, myName);
          const medalColor =
            rank === 1 ? "text-amber-500" :
            rank === 2 ? "text-slate-400" :
            rank === 3 ? "text-orange-400" : "text-slate-300";
          const score = Math.round(Number(row.score || 0) * 10) / 10;
          return (
            <li
              key={row.name || i}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                isMe
                  ? "bg-amber-100 border-amber-400 ring-2 ring-amber-400 shadow-sm"
                  : isPodium
                    ? "bg-white border-sky-200 shadow-sm"
                    : "bg-white/60 border-slate-200"
              }`}
              data-testid={isMe ? `vsh-ranking-row-me-${rank}` : `vsh-ranking-row-${rank}`}
            >
              <div className="w-8 flex justify-center">
                {isPodium ? <Medal className={`w-5 h-5 ${medalColor}`} /> : <span className="text-sm font-bold text-slate-500">#{rank}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-[Fredoka] font-bold text-slate-800 truncate flex items-center" data-testid={`vsh-ranking-name-${rank}`}>
                  <span className="truncate">{row.name || "—"}</span>
                  {isMe && <MeBadge lang={lang} />}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {row.total} {row.total === 1 ? t("duel_one") : t("duel_many")}
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-extrabold font-[Fredoka] ${score >= 0 ? "bg-indigo-500 text-white" : "bg-rose-500 text-white"}`}
                  data-testid={`vsh-ranking-score-${rank}`}
                  title={lang === "en" ? "Score = wins − losses × 0.5" : lang === "es" ? "Puntuación = victorias − derrotas × 0,5" : "Pontuação = vitórias − derrotas × 0,5"}
                >
                  <Trophy className="w-3 h-3" /> {score >= 0 ? "+" : ""}{score} pts
                </span>
                <div className="flex gap-1 text-[10px] font-bold">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                    <Trophy className="w-2.5 h-2.5" /> {row.wins}
                  </span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                    ✗ {row.losses}
                  </span>
                  {row.ties > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      = {row.ties}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
